import React, { useEffect, useRef, useState } from "react";
import {
  useLoaderData,
  useOutletContext,
  useRouteLoaderData,
} from "react-router-dom";
import { BACKEND_URL } from "../../helpers/variable";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useFormContext } from "../../component/FormContext";
import { DataGrid } from "@mui/x-data-grid";
import {
  DEFAULT_TEXTFIELD_STYLES,
  months,
  SLOT_PROPS,
} from "../../component/Header";
import { enqueueSnackbar } from "notistack";
import { useAction } from "../../component/ActionContext";
import { validateToken } from "../../API/nimble";

const CommentList = ["All", "Favorable", "Adverse", "Mixed","Others"];

const SurveyReport = () => {
  const { autoFetch ,urlInfo, clientNameUrlVersion} = useRouteLoaderData("layout");
  const { PCS} = useOutletContext();
  const { setActions } = useAction();

  const { formValues } = useFormContext();
  const { corporation, pc, year, month, readyToFetch } = formValues;

  const [loading, setLoading] = useState(false);
  const [surveyData, setSurveyData] = useState({ survey_responses: [] });
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [commentType, setCommentType] = useState("All");
  const fetchCondition =
    year === "" ||
    month === "" ||
    !corporation ||
    (PCS.length > 0 && pc === "");

  useEffect(() => {
    if (autoFetch && !fetchCondition && readyToFetch) {
      getSurveyData();
    }
  }, [autoFetch, fetchCondition, readyToFetch]);

  async function getSurveyData() {
    if (corporation.ID === "" && corporation.Name === "--select--") {
      enqueueSnackbar("Please select the Corporation from the list", {
        variant: "error",
      });
      return;
    }
    if (fetchCondition) {
      enqueueSnackbar("Missing required field: profit center", {
        variant: "error",
      });
      return;
    }

    setHasSubmitted(true);
    setLoading(true);

    const tokenValidate = await validateToken(clientNameUrlVersion);
    if (!tokenValidate) { return; }

    let res = await fetch(`${BACKEND_URL}/custom_report_survey`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        year,
        month: String(months.indexOf(month) + 1),
        corporation_id: corporation?.ID,
        comment_type: commentType.toLowerCase(),
        profit_center_id: pc,
      }),
    });
    if (res.status === 200) setSurveyData(await res.json());
    else if (res.status === 204) {
      enqueueSnackbar("No Data Found", {
        variant: "error",
      });
      setSurveyData({ survey_responses: [] });
    } else
      enqueueSnackbar(await res.text(), {
        variant: "error",
      });

    setLoading(false);
  }

  useEffect(() => {
    setActions(
      <>
        <TextField
          sx={{ ...DEFAULT_TEXTFIELD_STYLES }}
          select
          label="Comment Type"
          value={commentType}
          onChange={(e) => {
            setCommentType(e.target.value);
          }}
          slotProps={SLOT_PROPS}
        >
          {CommentList.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <Button variant="contained" onClick={getSurveyData}>
          Submit
        </Button>
      </>
    );
  }, [formValues, commentType, PCS]);

  return (
    <DataGrid
      rows={surveyData?.survey_responses || []}
      getRowId={(row) => row.response_id}
      showCellVerticalBorder
      showColumnVerticalBorder
      disableRowSelectionOnClick
      pagination
      getRowHeight={() => "auto"}
      slots={{
        noRowsOverlay: () =>
          hasSubmitted ? (
            <Box
              height="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Typography variant="body1">No Data Found</Typography>
            </Box>
          ) : null,
      }}
      sx={{
        "& .MuiTablePagination-selectLabel": {
          fontSize: 0,
          position: "relative",
          "&::after": {
            content: '"Comments per page :"',
            whiteSpace: "nowrap",
            fontSize: "0.875rem",
            position: "absolute",
            top: -13,
            left: -140,
          },
        },
      }}
      columns={[
        {
          headerName: "Date",
          field: "response_date",
          valueFormatter: (v) => (v ? new Date(v).toLocaleDateString() : "N/A"),
        },
        {
          headerName: "Sentiment",
          field: "sentiment",
          valueFormatter: (v) =>
            v ? v.charAt(0).toUpperCase() + v.slice(1) : "N/A",
        },
        {
          headerName: "Over all review Comment",
          field: "overall_review_comment",
          flex: 2,
          valueFormatter: (v) => (v ? v : "N/A"),
        },
      ]}
      loading={loading}
    />
  );
};

export default SurveyReport;
